import api from '../config/api';

export interface Marabout {
  id: number;
  nom_complet: string;
  nom_arabe?: string;
  photo?: string;
  biographie: string;
  date_naissance?: string;
  date_deces?: string;
  lieu_naissance?: string;
  periode?: string;
  nombre_livres: number;
}

export interface Livre {
  id: number;
  titre: string;
  auteur?: Marabout;
  auteur_autre?: string;
  auteur_nom?: string;
  categorie: string;
  langue: string;
  description?: string;
  nombre_pages?: number;
  couverture?: string;
  fichier_pdf?: string;
  date_publication?: number;
  isbn?: string;
}

export interface Sourate {
  id: number;
  numero: number;
  nom_francais: string;
  nom_arabe: string;
  nombre_versets: number;
  type_sourate: string;
}

export interface Verset {
  id: number;
  sourate: number;
  sourate_nom?: string;
  numero: number;
  texte_arabe: string;
  traduction_francais?: string;
  audio?: string;
}

export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next: string | null;
  previous: string | null;
}

export const bibliothequeService = {
  // Marabouts
  getMarabouts: async (page: number = 1, pageSize: number = 6, filters?: { search?: string }): Promise<PaginatedResponse<Marabout>> => {
    const params: any = { page, page_size: pageSize };
    if (filters?.search) {
      params.search = filters.search;
    }
    const response = await api.get('/bibliotheque/marabouts/', { params });
    if (response.data.results) {
      return {
        results: response.data.results,
        count: response.data.count || response.data.results.length,
        next: response.data.next || null,
        previous: response.data.previous || null
      };
    }
    // Fallback pour compatibilité
    const allData = Array.isArray(response.data) ? response.data : [response.data];
    return {
      results: allData,
      count: allData.length,
      next: null,
      previous: null
    };
  },

  getMaraboutById: async (id: number): Promise<Marabout> => {
    const response = await api.get(`/bibliotheque/marabouts/${id}/`);
    return response.data;
  },

  // Livres
  getLivres: async (page: number = 1, pageSize: number = 6, filters?: { categorie?: string; langue?: string; auteur?: number; search?: string }): Promise<PaginatedResponse<Livre>> => {
    const params: any = { page, page_size: pageSize };
    if (filters?.categorie) params.categorie = filters.categorie;
    if (filters?.langue) params.langue = filters.langue;
    if (filters?.auteur) params.auteur = filters.auteur;
    if (filters?.search) params.search = filters.search;
    const response = await api.get('/bibliotheque/livres/', { params });
    if (response.data.results) {
      return {
        results: response.data.results,
        count: response.data.count || response.data.results.length,
        next: response.data.next || null,
        previous: response.data.previous || null
      };
    }
    // Fallback pour compatibilité
    const allData = Array.isArray(response.data) ? response.data : [response.data];
    return {
      results: allData,
      count: allData.length,
      next: null,
      previous: null
    };
  },

  getLivreById: async (id: number): Promise<Livre> => {
    const response = await api.get(`/bibliotheque/livres/${id}/`);
    return response.data;
  },

  // Sourates avec pagination
  getSourates: async (page: number = 1, pageSize: number = 8): Promise<{ results: Sourate[]; count: number; next: string | null; previous: string | null }> => {
    const response = await api.get('/bibliotheque/sourates/', {
      params: { page, page_size: pageSize }
    });
    // Si pagination activée, retourner les données paginées
    if (response.data.results) {
      return {
        results: response.data.results,
        count: response.data.count || response.data.results.length,
        next: response.data.next || null,
        previous: response.data.previous || null
      };
    }
    // Sinon, retourner toutes les données sans pagination
    const allData = Array.isArray(response.data) ? response.data : [response.data];
    return {
      results: allData,
      count: allData.length,
      next: null,
      previous: null
    };
  },

  getSourateById: async (id: number): Promise<Sourate> => {
    const response = await api.get(`/bibliotheque/sourates/${id}/`);
    return response.data;
  },

  getVersetsBySourate: async (id: number, useApi: boolean = true, translation: string = 'fr.hamidullah', reciter: string = 'ar.abdulbasitmurattal'): Promise<Verset[]> => {
    const params: any = {};
    if (useApi) {
      params.api = 'true';
      params.translation = translation;
      params.reciter = reciter;
    }
    const response = await api.get(`/bibliotheque/sourates/${id}/versets/`, { params });
    return response.data;
  },

  // Synchroniser les sourates depuis l'API
  syncSouratesFromAPI: async (): Promise<{ message: string; sourates_count: number }> => {
    const response = await api.get('/bibliotheque/sourates/sync_from_api/');
    return response.data;
  },

  // Obtenir les récitateurs disponibles
  getAvailableReciters: async (): Promise<Array<{ code: string; name: string; name_ar: string }>> => {
    const response = await api.get('/bibliotheque/sourates/available_reciters/');
    return response.data;
  },

  // Obtenir l'URL audio de la sourate complète pour un récitateur
  getSourateAudioUrl: async (sourateId: number, reciter: string): Promise<string> => {
    const response = await api.get(`/bibliotheque/sourates/${sourateId}/audio_url/`, {
      params: { reciter }
    });
    return response.data.audio_url;
  },
};

